import { VisitorVariations } from '@src/container/handleIframeMessage/types';
import { Dispatch, ReactNode } from 'react';

export enum FsTargetingOperator {
  notEquals = 'NOT_EQUALS',
  notContains = 'NOT_CONTAINS',
  noExists = 'NOT_EXISTS',
  equals = 'EQUALS',
}

export enum UserRole {
  ACCOUNT_OWNER = 'ACCOUNT_OWNER',
  GUEST = 'GUEST',
  MEMBER = 'MEMBER',
  PROJECT_MANAGER = 'PROJECT_MANAGER',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

export type Targetings = {
  operator: FsTargetingOperator;
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

export type FsVariation = {
  id: string;
  name?: string;
  modifications: {
    type: string;
    value: unknown;
  };
  allocation?: number;
  reference?: boolean;
  isOrigin?: boolean;
  variationGroupId?: string;
  variationGroupName?: string;
};

export enum CampaignType {
  ab = 'ab',
  toggle = 'toggle',
  perso = 'perso',
  deployment = 'deployment',
}

export type FsVariationToForce = {
  campaignId: string;
  campaignName: string;
  campaignType: CampaignType;
  CampaignSlug?: string | null;
  variationGroupId: string;
  variationGroupName?: string;
  variation: FsVariation;
};

export type TargetingGroups = {
  targetings: Targetings[];
};

export type VariationGroupDTO = {
  id: string;
  name?: string;
  targeting: {
    targetingGroups: TargetingGroups[];
  };
  variations: FsVariation[];
};

export type FsCampaign = {
  id: string;
  name: string;
  type: CampaignType;
  slug?: string | null;
  variationGroups: VariationGroupDTO[];
};

export type UserAccess = {
  items: {
    id: string;
    role: UserRole;
    account_environment: {
      id: string;
      is_main: boolean;
      environment: string;
      account: {
        id: string;
        name: string;
      };
    };
  }[];
  current_items_count: number;
  current_page: number;
  total_count: number;
  items_per_page: number;
  last_page: number;
};

export type BucketingDTO = {
  panic?: boolean;
  campaigns?: FsCampaign[];
  accountSettings?: {
    enabledXPC?: true;
    troubleshooting?: {
      startDate: string;
      endDate: string;
      traffic: number;
      timezone: string;
    };
  };
};

export type LoadingStatus = 'fulfilled' | 'rejected' | 'pending';

export type User = {
  phone: null;
  validated: true;
  username: string;
  prefersColorScheme: string;
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  linkedin: string | null;
  roles: string[];
  company: null;
  department: null;
  jobTitle: null;
};

export type UserData = { user: User; access?: UserAccess };

export type FsAuthContextValue = {
  userData?: UserData;
  addUserData: (arg: UserData) => void;
};

export type UserRestrictedProps = {
  children: JSX.Element;
  toggleRetryAuth: () => void;
  retryAuth?: boolean;
};

export type AuthProviderProps = {
  children: ReactNode;
};

export type FsDataState = {
  bucketingFile?: BucketingDTO;
  visitorVariations?: Record<string, VisitorVariations>;
  visitorAllocatedCampaign: FsCampaign[];
  fsEnvId: string;
  visitorExposedCampaign: FsCampaign[];
  fsExposedCampaigns: FsCampaign[];
  fsUnExposedCampaigns: FsCampaign[];
  visitorExposedVariations?: Record<string, VisitorVariations>;
};

export enum VisitorVariationUpdateParam {
  NewNavigation = 'newNavigation',
}

export type FsReducerActionUpdateVisitorVariation = {
  type: 'updateVisitorVariations' | 'updateVisitorExposedVariations';
  value: Record<string, VisitorVariations>;
  param?: VisitorVariationUpdateParam;
};

export type FsSearchCampaigns = { type: 'searchFsCampaigns'; value: string };
export type FsReducerAction =
  | { type: 'setBucketingFile'; bucketingFile: BucketingDTO }
  | FsSearchCampaigns
  | FsReducerActionUpdateVisitorVariation;

export type FsContextValue = {
  dispatch: Dispatch<FsReducerAction>;
  fsDataState: FsDataState;
};

export type FsVariationToForceState = {
  variationsToForce: Record<string, FsVariationToForce>;
  variationsToForceTemp: Record<string, FsVariationToForce>;
};

export type FsVariationToForceReducerAction =
  | {
      type: 'addVariationToForceTemp';
      value: Record<string, FsVariationToForce>;
    }
  | {
      type: 'addVariationToForce';
      value: Record<string, FsVariationToForce>;
    }
  | {
      type: 'clearVariationsToForce';
    };

export type FsVariationToForceContextValue = {
  fsVariationToForceState: FsVariationToForceState;
  dispatchFsVariationToForce: Dispatch<FsVariationToForceReducerAction>;
};

export type FsCampaignData = {
  variations: FsVariation[];
  targetingGroups: {
    targetings: Targetings[];
  }[];
};

export type FsEventState = {
  hits: Record<string, unknown>[];
};

export type FsEventReducerAction =
  | {
      type: 'add';
      value: Record<string, unknown>[];
    }
  | { type: 'clearHits' };

export type FsEventContextValue = {
  fsHitsState: FsEventState;
  fsHitsDispatch: Dispatch<FsEventReducerAction>;
};
export type ShowToast = {
  text?: string;
  type?: 'SUCCESS' | 'WARNING' | 'DANGER' | 'INFO';
  dataTestId?: string;
  className?: string;
};

export type ToastInitialValue = {
  showToast: (arg: ShowToast) => void;
};

export type WrapperPromise<T> = {
  read(): T | undefined;
};
